import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/Ui/Button';
import { Loader } from '@/components/Ui/Loader';
import auth from '@/helpers/auth';

import { twoFaSchema, type TwoFaValues } from './Google2faPage.schema';

const Google2faPage = function () {
    const navigate = useNavigate();
    const [
        searchParams,
    ] = useSearchParams();
    const [
        t,
    ] = useTranslation('pages');

    const [
        success,
        setSuccess,
    ] = useState(false);

    const [
        svgCode,
        setSvgCode,
    ] = useState<string | null>(null);

    // SAM-007 — when arriving from the login enroll path, the LoginToken
    // is passed via `?token=…`. The backend's `login-token` guard accepts
    // it on the 2FA enrollment endpoints in lieu of a session. When the
    // admin is already session-authenticated (refreshing their secret),
    // the token is absent and the session cookie carries the auth.
    const loginTokenHeaders = useMemo(() => {
        const token = searchParams.get('token');

        return token ? { 'x-login-token': token } : {};
    }, [
        searchParams,
    ]);

    const { handleSubmit, register } = useForm({
        resolver: zodResolver(twoFaSchema),
        defaultValues: { code: '' },
    });

    const confirm = async ({ code }: TwoFaValues) => {
        await axios.post(`/api/auth/2fa/register`, {
            code: code,
        }, { headers: loginTokenHeaders }).then(({ data }) => {
            auth.setUser(data);

            setSuccess(true);
        }).catch(() => {
            toast.error(t('Code incorrect, please try again'));
        });
    };

    useEffect(() => {
        axios.get<string>('/api/auth/2fa/qr-code', { headers: loginTokenHeaders }).then(({ data }) => {
            setSvgCode(data);
        });
    }, [
        loginTokenHeaders,
    ]);

    if (success) {
        return (
            <div className='flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8 max-w-md'>
                <div className='flex gap-x-6 justify-center'>
                    <div className='flex flex-col space-y-4 text-center'>
                        <div className='text-xl text-primary font-normal'>{t('Your account is now officially protected.')}</div>
                        <div className='text-foreground mb-8'>
                            <p>{t('We\'ve added an extra layer of security to your profile so you can shop with total peace of mind.')}</p>
                            <p className='my-4 font-semibold'>{t('What happens next?')}</p>
                            <p>{t('The next time you log in, we\'ll simply ask you to open your Authenticator app and enter the 6-digit code. It\'s like a digital key for your favorite boutique!')}</p>
                        </div>
                        <Button
                            onClick={ () => {
                                return navigate(auth.getInitialPath());
                            } }
                            type='button'
                        >
                            <ArrowRightIcon aria-hidden='true' className='h-5 w-5 text-white' />
                            {t('Go to home page')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='min-h-full items-center justify-center max-w-4xl'>
            <div className='grid grid-cols-3 gap-8'>
                <div className='col-span-1'>
                    <div className='flex w-full justify-center'>
                        {!svgCode && <Loader />}

                        {!!svgCode && <img alt='' height='300' src={ `data:image/svg+xml;base64,${ btoa(svgCode) }` } width='300' /> }
                    </div>
                </div>
                <div className='col-span-2'>
                    <p className='font-bold text-xl'>{t('Let\'s Secure Your Account')}</p>
                    <p>{t('To keep your orders and personal details safe, we use a quick 2-step verification. It only takes a minute!')}</p>

                    <div className='bg-muted border-border border p-4 mt-8 rounded-lg text-sm'>
                        <p className='font-bold text-base'>{t('Step 1: Get the App')}</p>
                        <p>{t('If you haven\'t already, download a free Authenticator app (like Google Authenticator or Microsoft Authenticator) from your phone\'s App Store.')}</p>

                        <p className='font-bold text-base mt-4'>{t('Step 2: Link Your Account')}</p>
                        <ul className='list-decimal list-inside'>
                            <li>{t('Open the Authenticator app on your phone.')}</li>
                            <li>{t('Tap the "+" (plus) icon or the "Add Account" button.')}</li>
                            <li>{t('Choose "Scan a QR Code."')}</li>
                            <li>{t('Point your phone camera at the colorful square on this screen.')}</li>
                        </ul>

                        <p className='font-bold text-base mt-4'>{t('Step 3: Verify')}</p>
                        <p>{t('Once scanned, a 6-digit code will appear in your app next to our store\'s name. Enter that code below to finish!')}</p>
                    </div>
                    <form className='w-full justify-center mt-10' onSubmit={ handleSubmit(confirm) }>
                        <div className='flex gap-x-6 justify-center w-3/4'>
                            <input
                                className='relative block w-full appearance-none rounded-md border border-input px-3 py-2 text-foreground placeholder-muted-foreground ring-offset-primary focus-visible:outline-none focus:z-10 focus-visible:ring-1 focus:outline-none focus-visible:ring-offset-1 sm:text-sm'
                                id='code'
                                placeholder={ t('Authentication code') }
                                type='text'
                                { ...register('code') }
                            />
                            <button
                                className='group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 btn-primary w-full'
                                type='submit'
                            >
                                <ArrowRightIcon aria-hidden='true' className='h-5 w-5' />
                                {t('Confirm 2FA code')}
                            </button>
                        </div>
                        <button
                            className='group relative mt-8 flex gap-x-2 w-full justify-end text-sm rounded-md border border-transparent bg-transparent hover:text-primary'
                            onClick={ () => {
                                auth.clearSession();

                                return navigate('/login');
                            } }
                            type='button'
                        >
                            <ArrowLeftIcon aria-hidden='true' className='h-5 w-5' />
                            {t('Go back to login')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Google2faPage;
